'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { format } from 'date-fns';
import { ArrowLeft, Calendar, MessageSquare, Reply, Send } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { LogoRegister, LogoSlogan, LogoText } from '@/components/common';
import CommonNav from '@/components/layouts/CommonNav';
import AppFooter from '@/components/layouts/AppFooter';
import z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { handleApiErrors } from '@/lib/api-error-utils';
import { showToast } from '@/lib/toast';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Loader2 } from 'lucide-react';
import { Mail } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { useSession } from 'next-auth/react';
import { LogoLinks } from '@/components/common/smartguy-logo';
import { revalidateServerPage } from '@/lib/revalidate';
import { Heart } from 'lucide-react';

export const commentSchema = z.object({
  content: z.string('Comment is required'),
});

export default function CommunityForumsDetail({ data }) {
  const { data: session } = useSession();
  const accessToken = session?.accessToken;
  const post = data?.data;
  const comments = data?.data?.comments ? data.data.comments : [];

  // Initialize forms for each step
  const commentForm = useForm({
    resolver: zodResolver(commentSchema),
    defaultValues: { content: '' },
  });

  const [commentText, setCommentText] = useState('');
  const [copied, setCopied] = useState(false);
  const [key, setKey] = useState(false);

  const formatRelative = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    const minutes = Math.floor(diffMs / (1000 * 60));
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;

    return format(date, 'MMM d, yyyy');
  };

  const getInitials = (name = 'U') => {
    return name
      .split(' ')
      .map((part) => part[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  const handleAddComment = async (formData) => {
    let payload = {
      content: formData.content,
      communityForum: post._id,
    };

    try {
      const response = await fetch(`${process.env.NEXT_PUBLIC_API_URL}/community-forum-comments`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });
      const data = await response.json();

      if (response.ok) {
        commentForm.reset();

        showToast(data?.message || 'Success', 'success');
      } else {
        if (response.status === 400 || response.status === 409) {
          // showToast("Invalid email address", "error");
          handleApiErrors(commentForm, data.errors);
          return;
        }
        showToast(data?.message || 'An error occurred while adding the comment', 'error');
        return;
      }
      return;
    } catch (error) {
      showToast(error?.message || 'An error occurred while adding the comment', 'error');
    }
  };

  const shareLinks = (post) => ({
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(`https://www.smartguy.com/community/${post?.key}`)}`,
    whatsapp: `https://wa.me/?text=${encodeURIComponent(`https://www.smartguy.com/community/${post?.key}`)}`,
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(`https://www.smartguy.com/community/${post?.key}`)}&text=${encodeURIComponent('Check this out!')}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(`https://www.smartguy.com/community/${post?.key}`)}`,
    url: `https://www.smartguy.com/community/${post?.key}`,
  });

  const openShare = (url) => {
    window.open(url, '_blank', 'width=600,height=400');
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareLinks.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      showToast('Link successfully copied', 'success');
    } catch (err) {}
  };

  const likePosts = async (query) => {
    try {
      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_URL}/community-forums/${query}/like`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      const data = await response.json();

      if (response.ok) {
        await revalidateServerPage(`/community`);

        showToast(data?.message || 'Success', 'success');
      } else {
        showToast(data?.message || 'An error occurred during signin', 'error');
        return;
      }
      return;
    } catch (error) {
      showToast(error?.message || 'An error occurred during signin', 'error');
    }
  };

  return (
    <>
      <div>
        <div className="flex flex-col items-center justify-center pt-3">
          <Link href="/" className="flex flex-col items-center justify-center">
            <div className="flex">
              <LogoText className="text-3xl" />
              <LogoRegister />
            </div>
            <LogoSlogan className="me-4 text-center text-[.7rem]" />
          </Link>

          <LogoLinks />
        </div>
        <CommonNav />
      </div>

      <main className="min-h-screen bg-slate-50 px-4 py-6 text-slate-900 sm:px-6">
        <div className="mx-auto w-full max-w-3xl space-y-5">
          <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_12px_35px_rgba(15,23,42,0.08)]">
            <Link
              href="/community"
              className="mb-4 inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 transition hover:text-slate-900"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Community
            </Link>

            <div className="flex items-start gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">
                    SmartGuy Community
                  </h1>
                  <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                    Public
                  </span>
                </div>
              </div>
            </div>

            <Separator className="my-4 bg-slate-200" />

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
              <div className="mb-3 flex items-center justify-between gap-2">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-slate-200 px-3 py-1 text-xs font-semibold text-slate-700 uppercase">
                  <MessageSquare className="h-3.5 w-3.5" />
                  Forum Topic
                </div>

                <span className="flex items-center gap-1 text-xs text-slate-500">
                  <Calendar className="h-3.5 w-3.5" />
                  {post?.createdAt ? format(new Date(post.createdAt), 'MMMM d, yyyy') : '-'}
                </span>
              </div>

              <p className="text-[15px] leading-7 text-slate-700">{post?.description || '-'}</p>

              {post?.imageUrl && (
                <div className="relative mt-4 h-[220px] w-full overflow-hidden rounded-xl bg-slate-200 sm:h-[340px]">
                  <Image
                    src={`${process.env.NEXT_PUBLIC_S3_BUCKET_URL}/${post.imageUrl}`}
                    alt="Community topic image"
                    fill
                    sizes="(max-width: 768px) 100vw, 768px"
                    className="object-cover"
                  />
                </div>
              )}
            </div>

            <div className="mt-5 flex flex-col items-start justify-between sm:flex-row sm:items-center">
              <div className="flex items-center gap-1">
                {post.isLiked ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="gap-1.5 rounded-full text-xs text-slate-600 hover:scale-[1.2] hover:bg-transparent"
                    onClick={() => likePosts(post._id)}
                  >
                    <Heart className="h-6 w-6 fill-red-500 text-red-500" />
                    {post.totalLikes > 0 && <span>{post.totalLikes}</span>}
                    Like
                  </Button>
                ) : !session?.user ? (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="gap-1.5 rounded-full px-3 text-xs text-slate-600 hover:bg-transparent hover:text-slate-600"
                      >
                        <Heart className="h-3.5 w-3.5 transition-all hover:scale-[1.2]" />
                        {post.totalLikes > 0 && <span>{post.totalLikes}</span>}
                        Like
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Want to join the conversation?</AlertDialogTitle>
                        <AlertDialogDescription>Sign in to continue</AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogAction>
                          <Link href={`/signin?callbackUrl=/community/${post?.key}`}>Sign in</Link>
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 gap-1.5 rounded-full px-3 text-xs text-slate-600 transition-all hover:scale-[1.2] hover:bg-transparent hover:text-slate-600"
                    onClick={() => likePosts(post._id)}
                  >
                    <Heart className="h-3.5 w-3.5" />
                    {post.totalLikes > 0 && <span>{post.totalLikes}</span>}
                    Like
                  </Button>
                )}
              </div>

              <div className="me-2 flex flex-col items-start justify-end space-y-2 gap-x-3 lg:flex-row lg:items-center lg:space-y-0">
                <div className="">
                  <p className="text-[.75rem] text-black">Share this now</p>
                </div>
                <div className="flex gap-x-3">
                  <div
                    onClick={() => openShare(shareLinks(post).facebook)}
                    className="relative flex size-8 cursor-pointer items-center justify-center rounded-full bg-gray-200 transition-all duration-300 hover:scale-110 hover:shadow-md"
                  >
                    <Image
                      src="/assets/icons/facebook.svg"
                      alt="Facebook"
                      width={22}
                      height={22}
                      className="transition-transform duration-300"
                    />
                  </div>

                  <div
                    onClick={() => openShare(shareLinks(post).whatsapp)}
                    className="relative flex size-8 cursor-pointer items-center justify-center rounded-full bg-gray-200 transition-all duration-300 hover:scale-110 hover:shadow-md"
                  >
                    <Image
                      src="/assets/icons/whatsapp.svg"
                      alt="whatsapp"
                      width={22}
                      height={22}
                      className="transition-transform duration-300"
                    />
                  </div>

                  <div
                    onClick={() => openShare(shareLinks(post).twitter)}
                    className="relative flex size-8 cursor-pointer items-center justify-center rounded-full bg-gray-200 transition-all duration-300 hover:scale-110 hover:shadow-md"
                  >
                    <Image
                      src="/assets/icons/twitter.svg"
                      alt="twitter"
                      width={20}
                      height={20}
                      className="pt-1 transition-transform duration-300"
                    />
                  </div>

                  <div
                    onClick={() => openShare(shareLinks(post).linkedin)}
                    className="relative flex size-8 cursor-pointer items-center justify-center rounded-full bg-gray-200 transition-all duration-300 hover:scale-110 hover:shadow-md"
                  >
                    <Image
                      src="/assets/icons/linkedin.svg"
                      alt="linkedin"
                      width={22}
                      height={22}
                      className="transition-transform duration-300"
                    />
                  </div>

                  <div
                    onClick={() => copyToClipboard(shareLinks(post).url)}
                    className="relative flex size-8 cursor-pointer items-center justify-center rounded-full bg-gray-200 transition-all duration-300 hover:scale-110 hover:shadow-md"
                  >
                    {copied && key === post.key ? (
                      <Check className={`h-6 w-6 transition-transform duration-300`} />
                    ) : (
                      <Image
                        src="/assets/icons/share-link.svg"
                        alt="share link"
                        width={22}
                        height={22}
                        className="transition-transform duration-300"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>

          <Card className="rounded-2xl border border-slate-200 bg-white py-0 shadow-[0_10px_28px_rgba(15,23,42,0.08)]">
            <CardContent className="space-y-4 p-5">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Join the Discussion</h2>
                <span className="text-xs text-slate-500">{comments.length} comments</span>
              </div>

              <Form {...commentForm}>
                <form onSubmit={commentForm.handleSubmit(handleAddComment)} className="space-y-4">
                  <FormLabel className="w-full text-center text-gray-700">
                    Please enter your comment:
                  </FormLabel>
                  <FormField
                    control={commentForm.control}
                    name="content"
                    render={({ field }) => (
                      <FormItem className="space-y-2">
                        <div className="relative">
                          <FormControl>
                            <div>
                              {!session?.user ? (
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Textarea
                                      id="content"
                                      placeholder="Type your comment here."
                                      {...field}
                                      className={
                                        'min-h-28 resize-y border-slate-300 bg-white text-slate-800 placeholder:text-slate-400'
                                      }
                                    />
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>
                                        Want to join the conversation?
                                      </AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Sign in to continue
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogAction>
                                        <Link href={`/signin?callbackUrl=/community/${post?.key}`}>
                                          Sign in
                                        </Link>
                                      </AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              ) : (
                                <Textarea
                                  id="content"
                                  placeholder="Type your comment here."
                                  {...field}
                                  className={
                                    'min-h-28 resize-y border-slate-300 bg-white text-slate-800 placeholder:text-slate-400'
                                  }
                                />
                              )}
                            </div>
                          </FormControl>
                        </div>
                        <FormMessage className="text-sm text-red-600" />
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-end gap-3">
                    <Button type="submit" variant="default" className="rounded-full">
                      <Send className="h-4 w-4" />
                      Post Comment
                      {commentForm.formState.isSubmitting && (
                        <>
                          <Loader2 className="ml-2 size-6 animate-spin" />
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>

          <section className="space-y-3">
            {comments.map((comment, index) => (
              <Card
                key={index}
                className="rounded-2xl border border-slate-200 bg-white py-0 shadow-[0_8px_20px_rgba(15,23,42,0.06)]"
              >
                <CardContent className="space-y-4 p-5">
                  <div className="flex items-start gap-3">
                    <Avatar className="h-9 w-9">
                      <AvatarFallback className="bg-slate-200 text-xs font-semibold text-slate-700">
                        {getInitials(comment?.user?.firstName)}
                      </AvatarFallback>
                    </Avatar>

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-slate-900">
                          {comment?.user?.firstName}
                        </p>
                        <span className="text-xs text-slate-500">
                          {formatRelative(comment.createdAt)}
                        </span>
                      </div>
                      <p className="mt-2 text-sm leading-7 text-slate-700">{comment.content}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </section>
        </div>
      </main>

      <AppFooter />
    </>
  );
}
